import React, { useMemo } from "react";
import { PencilIcon, TrashIcon, RefreshIcon } from "@heroicons/react/outline";
import { CustomTable } from "../../../components/organisms/ShimmerTable";
import { EyeTrashIconButtons } from "../../../components/molecules/TableActionButtons";
import User from "../../../classes/User";
import { Badge } from "@tyrannosaurustech/ui";
import { Variant } from "@tyrannosaurustech/ui/build/src/types/Colors";

const ROLES = {
  admin: "RayNA Admin",
  clientmanager: "Admin",
  clientmember: "User",
};

interface IUsersTable {
  onEditUser: (user?: User) => void;
  onDeleteUser: (user?: User) => void;
  onResetPasswordUser: (user?: User) => void;
  onBulkAddUsers: () => void;
  users: User[];
  isFetching: boolean;
  loggedInUser: User | null;
}

const UsersTable: React.FC<IUsersTable> = ({
  onEditUser,
  onDeleteUser,
  onBulkAddUsers,
  onResetPasswordUser,
  isFetching,
  loggedInUser,
  users,
}) => {
  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        filterName: "name",
        includeToolTip: true,
      },
      {
        Header: "Email",
        accessor: "email",
        filterName: "email",
        includeToolTip: true,
      },
      {
        Header: "Role",
        accessor: "roles",
        filterName: "roles",
        Cell: (cell) => {
          return ROLES[cell.row?.original?.roles];
        },
      },
      {
        Header: "Status",
        accessor: "isActive",
        filterName: "isActive",
        headerClassName: "text-center",
        Cell: (cell) => {
          const isActive = cell.row?.original?.isActive;
          let variant: Variant = isActive ? "success" : "warning";
          let label = isActive ? "Active" : "Pending";
          return (
            <Badge
              rounded={false}
              variant={variant}
              label={label}
              base="tertiary"
            />
          );
        },
      },
      {
        Header: "Actions",
        accessor: "id",
        disableSortBy: true,
        disableFilters: true,
        Cell: (cell) => {
          const userId = cell.row.original.id;
          let icons = [PencilIcon];
          icons.push(RefreshIcon);
          if (loggedInUser?.id !== userId) icons.push(TrashIcon);
          return (
            <>
              <EyeTrashIconButtons
                icons={icons}
                onClick1={() => onEditUser(cell.row.original)}
                onClick2={() => onResetPasswordUser(cell.row.original)}
                onClick3={() => onDeleteUser(cell.row.original)}
              />
            </>
          );
        },
      },
    ],
    [loggedInUser]
  );

  return (
    <CustomTable
      addNewButtonText="Add Users"
      tableType="users"
      canFilter={false}
      columns={columns}
      data={users}
      isLoading={isFetching}
      navigation={() => onBulkAddUsers()}
    />
  );
};

export default UsersTable;
